import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { CandidateNote, CandidateNoteType } from '@/types/candidates';
import { formatDistanceToNow } from 'date-fns';
import { Cloud, Pencil, Trash2 } from 'lucide-react';
import { CandidateNoteBody } from './candidate-note-body';

const TYPE_LABELS: Record<CandidateNoteType, string> = {
  INTERNAL: 'Internal',
  INTERVIEW_FEEDBACK: 'Interview feedback',
};

const TYPE_STYLES: Record<CandidateNoteType, string> = {
  INTERNAL: 'bg-muted text-muted-foreground',
  INTERVIEW_FEEDBACK: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
};

function initials(firstName: string, lastName: string): string {
  return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase() || '?';
}

function pfAuthorInitials(name: string | null, email: string | null): string {
  const source = name || email || 'PF';
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  const first = parts[0]?.[0] ?? 'P';
  const second = parts[1]?.[0] ?? 'F';
  return `${first}${second}`.toUpperCase();
}

interface CandidateNoteItemProps {
  note: CandidateNote;
  currentUserId: string | null;
  onEdit?: () => void;
  onDelete?: () => void;
  isDeleting?: boolean;
}

export function CandidateNoteItem({
  note,
  currentUserId,
  onEdit,
  onDelete,
  isDeleting = false,
}: CandidateNoteItemProps) {
  const isFromPF = note.source === 'peopleforce';
  // PF notes are deletable/editable by any user with view permission (the
  // original PF author often isn't in our employee directory). Native notes
  // remain author-only — matching the backend's authz rules.
  const canMutate = isFromPF
    ? true
    : note.authorId !== null && note.authorId === currentUserId;

  let authorName = 'System';
  if (note.author) {
    authorName = `${note.author.firstName} ${note.author.lastName}`.trim() || 'Unknown';
  } else if (isFromPF) {
    authorName = note.pfAuthorName || note.pfAuthorEmail || 'PeopleForce user';
  }

  return (
    <li className="group flex items-start gap-3 py-3">
      <Avatar className="h-9 w-9 shrink-0 rounded-full">
        {note.author?.avatarUrl ? (
          <AvatarImage src={note.author.avatarUrl} alt={authorName} />
        ) : null}
        <AvatarFallback className="rounded-full text-xs">
          {note.author
            ? initials(note.author.firstName, note.author.lastName)
            : isFromPF
              ? pfAuthorInitials(note.pfAuthorName, note.pfAuthorEmail)
              : 'S'}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground">{authorName}</span>
          <Badge
            variant="secondary"
            className={cn(
              'h-5 px-1.5 text-[10px] font-normal uppercase tracking-wide',
              TYPE_STYLES[note.noteType]
            )}
          >
            {TYPE_LABELS[note.noteType]}
          </Badge>
          {isFromPF && (
            <Badge
              variant="outline"
              className="h-5 gap-1 border-hygge/40 px-1.5 text-[10px] font-normal uppercase tracking-wide text-hygge"
              title={
                note.editedLocally
                  ? 'Imported from PeopleForce — edited locally, future syncs will skip it'
                  : 'Imported from PeopleForce'
              }
            >
              <Cloud className="h-2.5 w-2.5" />
              PeopleForce
              {note.editedLocally && <span className="text-muted-foreground">· edited</span>}
            </Badge>
          )}
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(note.createdAt), { addSuffix: true })}
          </span>
          {canMutate && (
            <div className="ml-auto flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
              {onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Edit note"
                  title="Edit note"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={isDeleting}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive disabled:cursor-wait disabled:opacity-50"
                  aria-label="Delete note"
                  title="Delete note"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
        <CandidateNoteBody body={note.body} className="mt-1" />
      </div>
    </li>
  );
}
